import { Button, Avatar, Tooltip } from "antd";
import { ColumnWidthOutlined, CheckCircleFilled } from "@ant-design/icons";
import { ConversationList } from "@components/chat/ConversationList";
import { FanPageHeader } from "@components/chat/FanPageHeader";
import { MessageList } from "@components/chat/MessageList";
import { ChatInput } from "@components/chat/ChatInput";
import { FanpageActiveSidebar } from "@components/chat/FanpageActiveSidebar";
import { SyncPage } from "@components/chat/SyncConversationPage";
import { FacebookTemplates } from "@components/settings/FacebookTemplates";
import { TemplateCreate } from "@components/settings/FacebookTemplates/TemplateCreate";
import { TemplateEdit } from "@components/settings/FacebookTemplates/TemplateEdit";
import type { Conversation, Message } from "@interfaces/chat.interface";
import { useChatStore } from "@stores/chatStore";
import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "@config/routes.config";
import { PLATFORM_GROUPS } from "@constants/platforms";
import classNames from "classnames";
import { getAvatarGradient, getAvatarLetter } from "@utils/avatarUtils";
import { AliasNameEditor } from "@components/chat/AliasNameEditor";
import styles from "./styles.module.less";

interface FanPageBodyProps {
  activeConversation: Conversation | undefined;
  messages: Message[];
  isFetching: boolean;
  loadMore: () => void;
  hasMore: boolean;
  onSend: (
    body: string,
    attachments?: Array<{
      attachmentId: string;
      type: "image" | "video" | "file";
      fileName: string;
      fileUrl?: string;
    }>
  ) => void;
  onDownload: (url: string) => void;
  isSidebarOpen: boolean;
  onToggleUserSidebar: () => void;
  onOpenFanpageMenu: () => void;
  pageId?: string | null;
  selectedFanpageName?: string;
  filteredConversations: Conversation[];
  messageSyncStatus?: "NOT_SYNCED" | "IN_PROGRESS" | "SYNCED";
  isShowInfoUser?: boolean;
}

export const FanPageBody = ({
  activeConversation,
  messages,
  isFetching,
  loadMore,
  hasMore,
  onSend,
  onDownload,
  isSidebarOpen,
  onToggleUserSidebar,
  onOpenFanpageMenu,
  pageId,
  selectedFanpageName,
  filteredConversations,
  messageSyncStatus,
  isShowInfoUser,
}: FanPageBodyProps) => {
  const location = useLocation();
  const conversationFolderStatusMap = useChatStore(
    (state) => state.conversationFolderStatusMap
  );
  const upsertConversation = useChatStore((state) => state.upsertConversation);
  const movedConversationInfo = useChatStore((state) => state.movedConversationInfo);
  const selectedConversationId = useChatStore((state) => state.selectedConversationId);
  const setMovedConversationInfo = useChatStore((state) => state.setMovedConversationInfo);

  // Clear movedConversationInfo on mount to prevent showing stale notifications after reload
  useEffect(() => {
    setMovedConversationInfo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - clear any stale notification from previous session

  const folderStatus = useMemo(() => {
    if (!activeConversation?.lastMessage?.conversationId) {
      return undefined;
    }
    return conversationFolderStatusMap[
      activeConversation.lastMessage.conversationId
    ];
  }, [activeConversation, conversationFolderStatusMap]);

  const isInProgress = messageSyncStatus === "IN_PROGRESS";
  const isNotSynced = messageSyncStatus === "NOT_SYNCED";

  const showNewPage =
    selectedFanpageName &&
    ((isInProgress && !activeConversation) ||
      (isNotSynced && !activeConversation));

  // Only show chat detail when a conversation is explicitly selected
  // Do not show chat window when conversations arrive from socket
  const canShowChatDetail = !!activeConversation;
  const isZaloConversation = activeConversation?.channel === "zalo";
  const aliasFromMeta =
    (activeConversation?.lastMessage?.meta?.conversationAliasName as
      | string
      | undefined) ||
    (activeConversation?.lastMessage?.meta?.alias as string | undefined);

  const renderSettingContent = () => {
    const settingGroup = PLATFORM_GROUPS.find((g) => g.id === "setting");
    const menuItem = settingGroup?.submenu?.find(
      (item) => item.path === location.pathname
    );

    if (!menuItem) return null;

    switch (menuItem.id) {
      case "facebook-templates":
        return <FacebookTemplates />;
      default:
        return (
          <div className={styles.settingContent}>
            <p>{menuItem.label}</p>
          </div>
        );
    }
  };

  const renderTemplatePages = () => {
    const pathname = location.pathname;

    if (pathname === ROUTES.SETTINGS.FACEBOOK_TEMPLATE_CREATE) {
      return <TemplateCreate />;
    }
    const editMatch = pathname.match(/^\/settings\/facebook-templates\/(\d+)$/);
    if (editMatch) {
      return <TemplateEdit />;
    }

    return null;
  };

  const isSettingsPage = location.pathname.startsWith("/settings");
  const templatePageContent = renderTemplatePages();
  return (
    <section className={styles.main}>
      <FanPageHeader
        conversation={activeConversation}
        typingUsers={activeConversation?.typingUsers}
        pageId={pageId}
        isShowInfoUser={isShowInfoUser}
      />
      <div className={styles.contentArea}>
        {isSettingsPage ? (
          <div className={styles.settingColumn}>
            {templatePageContent || renderSettingContent()}
          </div>
        ) : (
          <>
            <div className={styles.conversationColumn}>
              <ConversationList onOpenFanpageMenu={onOpenFanpageMenu} />
            </div>
            <div className={styles.messageColumn}>
              {showNewPage ? (
                <SyncPage
                  selectedFanpageName={selectedFanpageName || ""}
                  filteredConversations={filteredConversations}
                />
              ) : canShowChatDetail ? (
                <div className={styles.messageContent}>
                  {activeConversation && (
                    <>
                      <div className={styles.chatDetailHeader}>
                        <div className={styles.chatDetailInfo}>
                        {activeConversation.avatar ? (
                          <Avatar src={activeConversation.avatar} size={40} />
                        ) : (
                          <Avatar
                            style={{
                              background: getAvatarGradient(
                                activeConversation.name?.charAt(0) || "A"
                              ),
                              color: "#ffffff",
                              fontWeight: 600,
                              fontSize: "16px",
                            }}
                            size={40}
                          >
                            {getAvatarLetter(activeConversation.name)}
                          </Avatar>
                        )}
                        <div className={styles.chatDetailName}>
                          <AliasNameEditor
                            conversationId={activeConversation.id}
                            conversationName={activeConversation.name}
                            alias={aliasFromMeta}
                            isZalo={isZaloConversation}
                            onAliasUpdated={(newAlias) => {
                              if (!activeConversation) return;
                              const updatedConversation = {
                                ...activeConversation,
                                name: newAlias,
                                lastMessage: {
                                  ...activeConversation.lastMessage,
                                  meta: {
                                    ...(activeConversation.lastMessage.meta ||
                                      {}),
                                    conversationAliasName: newAlias,
                                  },
                                },
                              };
                              upsertConversation(updatedConversation);
                            }}
                            badge={
                              folderStatus === "DONE" ? (
                                <Tooltip title="Đánh dấu xong">
                                  <CheckCircleFilled
                                    className={styles.doneBadge}
                                  />
                                </Tooltip>
                              ) : null
                            }
                          />
                          {movedConversationInfo && 
                           activeConversation.lastMessage?.conversationId === movedConversationInfo.conversationId &&
                           selectedConversationId === activeConversation.id && (
                            <div className={styles.movedBanner}>
                              <span className={styles.movedBannerText}>
                                Đã di chuyển đến tab "{movedConversationInfo.targetTabLabel}"
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.chatDetailActions}>
                        {/* <Dropdown
                          menu={{
                            items: [
                              { key: "archive", label: "Lưu trữ" },
                              { key: "mute", label: "Tắt thông báo" },
                              { key: "delete", label: "Xóa", danger: true },
                            ] as MenuProps["items"],
                          }}
                          trigger={["click"]}
                        >
                          <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown> */}

                        <Button
                          type="text"
                          icon={<ColumnWidthOutlined />}
                          onClick={onToggleUserSidebar}
                          className={styles.toggleSidebarBtn}
                        />
                      </div>
                    </div>
                    </>
                  )}
                  {activeConversation && (
                    <>
                      <MessageList
                    messages={messages}
                    isLoading={isFetching}
                    onDownload={onDownload}
                    onLoadMore={loadMore}
                    hasMore={hasMore}
                    pageId={pageId}
                  />
                  <ChatInput
                    onSend={onSend}
                    pageId={pageId}
                    disabled={false}
                    activeConversation={activeConversation}
                  />
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.messageContent}></div>
              )}
              <aside
                className={classNames(styles.right, {
                  [styles.hidden]: !isSidebarOpen,
                })}
              >
                <FanpageActiveSidebar />
              </aside>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
